import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { ParseService } from 'apps/account/src/account-shared/services/parse.service';
import { dateSerialize } from 'apps/account/src/account-shared/pipes/date-serializer.util';
import { SpacesService } from 'account-hybrid/common/components/spaces';
import { IdentityService, SessionService } from 'account-hybrid/core/authentication';
import { SnapshotService } from 'apps/account/src/account-shared/services/snapshot.service';
import { EventBusService } from 'apps/account/src/account-shared/services/event-bus.service';
import { CampaignV1ApiService } from 'apps/account/src/account-shared/services/campaign-v1-api.service';
import * as JourneysActions from '../../../../account-v2/src/app/features/advocacy/group/journeys/actions';
import { AdvocacyNgReduxFacade } from '../store/advocacy-ngredux.facade';
import { NativeCampaignContainerService } from '../touchpoint-sheets/native-campaign-container.service';
import PhotoAdvocacyApi from '../../../../account-v2/src/app/actions/api/photoAdvocacyApi';
import PoststayApi from '../../../../account-v2/src/app/actions/api/poststayApi';
import PrestayApi from '../../../../account-v2/src/app/actions/api/prestayApi';
import PropertyApi from '../../../../account-v2/src/app/actions/propertyApi';

/**
 * Native-Angular port of the AngularJS `ftGuestsJourneys` controller
 * (account-v2/.../advocacy/group/journeys/journeys.component.ts).
 *
 * Drives the /advocacy/journeys leaf. Follows the bigPicture/contests/audiences
 * recipe — the NgRx store via the $ngRedux-shaped facade, action thunks as ES
 * imports, services fully native (inc 2s: the last $injector grab is gone).
 *
 * State slice this binds to (same shape as v1's `$ngRedux.connect`):
 *   { ...state.views.guestsJourneys,
 *     ...state.apiData.propertyGuestsJourneys, // prestay/poststay/photoAdvocacy/homepage/quoteListAdvocacy
 *     prestayCampaignUuid: state.apiData.propertyPlatformData.prestayCampaignUuid,
 *     poststayCampaignUuid: …,
 *     photoAdvocacyCampaignUuid: …,
 *     homepageCampaignUuid: …,
 *     quoteListCampaignUuid: …,
 *     prestayCampaigns: state.apiData.prestay,
 *     poststayCampaigns: state.apiData.poststay,
 *     photoCampaigns: state.apiData.photoAdvocacy,
 *     isFliptoStaff: identity.isFliptoStaff(),
 *     platformActivationStatus: { prestay/poststay/photoAdvocacy/homepage/quoteList booleans } }
 *
 * Most of the template (touchpoint panels, carousel cards, campaign editors,
 * stat displays) is gated on `platformActivationStatus.*` flags — all FALSE
 * at the QA-login parity property, so the screenshot captures only the
 * structural skeleton + section-header text. CUSTOM/NO_ERRORS_SCHEMA on
 * AdvocacyGroupModule already tolerates the AngularJS-only directives
 * inside those branches (ft-carousel-*, ft-touchpoint-*, ft-switch,
 * ft-campaign-list, ft-state, ft-state-class, ft-set-state,
 * ft-positional-class, ft-scroll-to). Replace each with a native shim when
 * a property with active advocacy gets recorded into the harness.
 */
@Component({
    selector: 'ft-guests-journeys',
    templateUrl: './journeys.component.html',
    standalone: false
})
export class JourneysComponent implements OnInit, OnDestroy {
  property: any;
  prestayCampaignUuid: any;
  poststayCampaignUuid: any;
  photoAdvocacyCampaignUuid: any;
  homepageCampaignUuid: any;
  quoteListCampaignUuid: any;
  prestayCampaigns: any;
  poststayCampaigns: any;
  photoCampaigns: any;
  isFliptoStaff = false;

  // Spread from state.apiData.propertyGuestsJourneys + state.views.guestsJourneys.
  prestayAdvocacy: any;
  poststayAdvocacy: any;
  photoAdvocacy: any;
  homepageAdvocacy: any;
  quoteListAdvocacy: any;
  prestayShareIncentive: any;
  prestaySignupIncentive: any;
  poststayShareIncentive: any;
  photoAdvocacySignupIncentive: any;
  warmLeadNurturing: any;
  isInitialized = false;

  prestayCampaignsSortMode = false;
  photoAdvocacyCampaignsSortMode = false;
  poststayCampaignsSortMode = false;

  platformActivationStatus: { prestay: boolean; poststay: boolean; photoAdvocacy: boolean; homepage: boolean; quoteList: boolean } = {
    prestay: false,
    poststay: false,
    photoAdvocacy: false,
    homepage: false,
    quoteList: false,
  };

  // v1 mutable section-fetch-status bag — gates each fetchSection helper.
  sectionDataRequestStatus = {
    prestay: false,
    poststay: false,
    photoContest: false,
    inspiration1: false,
    inspiration2: false,
    superchargers1: false,
    superchargers2: false,
    superchargers3: false,
  };

  /** Template binds to vm.sectionsController / vm.carouselCardsController for
   *  cross-component imperative calls. Loosely typed to mirror v1. */
  sectionsController: any;
  carouselCardsController: any;

  private unsubscribe?: () => void;
  private $ngRedux: any;
  private campaignContainer: any;
  // WS5 #10 inc 2r: native SnapshotService via a FIELD INITIALIZER (inject() in
  // ngOnInit throws NG0203). The dead 'PreStay' grab (assigned, never read) died here too.
  private snapshot = inject(SnapshotService);
  private dateSerializer: any;
  // WS5 #10 inc 2s: the native Campaign port (only .reorder is called here) —
  // the component's LAST $injector grab; the file is fully drained.
  private campaign = inject(CampaignV1ApiService);
  // ParseService + IdentityService via field initializers (a valid injection context);
  // the remaining service grabs live in ngOnInit, where inject() would throw NG0203.
  private identity = inject(IdentityService);
  private parse = inject(ParseService);
  // WS5 Phase 3 #4: native campaign-container launcher (createComponent mount),
  // replacing $injector.get('CampaignContainer'). Field initializer — inject() in
  // ngOnInit throws NG0203 (see reference_account_inject_ngoninit_ng0203).
  private nativeCampaignContainer = inject(NativeCampaignContainerService);
  private eventBus = inject(EventBusService);
  private offBus: Array<() => void> = [];
  /** The range the picker last asked the campaign lists for; the snapshot is only the starting range. */
  private campaignRange?: { start: Date; end: Date };

  /**
   * Same modal-scope-publisher trick AudiencesComponent uses — see its
   * jsdoc. Allows the in-template `<ft-touchpoint-trigger>` /
   * `<ft-touchpoint-panel>` / `<ft-modal>` / etc. shells (all gated
   * behind platformActivationStatus.* false at QA-login) to type-check
   * under AOT without per-element typed fields.
   */
  [key: string]: any;

  constructor(
    private readonly spacesService: SpacesService,
    private readonly sessionService: SessionService,
    private readonly advStore: AdvocacyNgReduxFacade,
  ) {}

  ngOnInit(): void {
    this.property = this.sessionService.getProperty(this.spacesService.getPropertyUuid()!);

    // WS2 strangle: advocacy state now in the native NgRx feature (facade).
    this.$ngRedux = this.advStore;
    this.campaignContainer = this.nativeCampaignContainer;
    this.dateSerializer = dateSerialize;
    this.isFliptoStaff = this.identity?.isFliptoStaff?.() ?? false;

    this.unsubscribe = this.$ngRedux.subscribe(() => this.syncFromState());
    this.syncFromState();
    this.fetchPrestaySection();

    // The touchpoint and campaign editors open over this page as modals and never re-run it, so it
    // has to be told when one of them changed something.
    this.offBus = [
      this.eventBus.on('touchpoint.statusChanged', (args) => this.$ngRedux.dispatch(PropertyApi.updateTouchpointStatus(args))),
      this.eventBus.on('campaign.changed', () => this.refreshCampaigns()),
      this.eventBus.on('campaign.statusChanged', () => this.refreshCampaigns()),
    ];
  }

  ngOnDestroy(): void {
    this.unsubscribe?.();
    this.offBus.forEach((off) => off());
  }

  /** The range every campaign list is asked for: what the picker last chose, else the starting range. */
  private rangeInView(): { start: string; end: string } {
    const { start, end } = this.campaignRange ?? this.snapshot.get();
    return { start: this.dateSerializer(new Date(start)), end: this.dateSerializer(new Date(end)) };
  }

  /** Refetch the three campaign lists for the range the page is showing. */
  private refreshCampaigns(): void {
    const { start, end } = this.rangeInView();
    this.$ngRedux.dispatch(JourneysActions.dateRangeChange(this.property.UUID, start, end));
  }

  private syncFromState(): void {
    const state = this.$ngRedux.getState();
    const platform = state?.apiData?.propertyPlatformData ?? {};
    this.prestayCampaignUuid = platform.prestayCampaignUuid;
    this.poststayCampaignUuid = platform.poststayCampaignUuid;
    this.photoAdvocacyCampaignUuid = platform.photoAdvocacyCampaignUuid;
    this.homepageCampaignUuid = platform.homepageCampaignUuid;
    this.quoteListCampaignUuid = platform.quoteListCampaignUuid;
    this.prestayCampaigns = state?.apiData?.prestay;
    this.poststayCampaigns = state?.apiData?.poststay;
    this.photoCampaigns = state?.apiData?.photoAdvocacy;

    const journeys = state?.apiData?.propertyGuestsJourneys ?? {};
    Object.assign(this, journeys);
    Object.assign(this, state?.views?.guestsJourneys ?? {});

    this.platformActivationStatus = {
      prestay: !!journeys.prestayAdvocacy,
      poststay: !!journeys.poststayAdvocacy,
      photoAdvocacy: !!journeys.photoAdvocacy,
      homepage: !!journeys.homepageAdvocacy,
      quoteList: !!journeys.quoteListAdvocacy,
    };
  }

  // section-fetch helpers — same shape as v1.
  fetchSectionData(sectionName: string): any {
    const map: Record<string, () => any> = {
      prestay: () => this.fetchPrestaySection(),
      poststay: () => this.fetchPoststaySection(),
      photocontest: () => this.fetchPhotoContestSection(),
      superchargers2: () => this.fetchHomepageSection(),
      superchargers3: () => this.fetchQuoteListSection(),
    };
    return map[sectionName]?.();
  }

  fetchPhotoContestSection(): Promise<any> | undefined {
    if (this.sectionDataRequestStatus.photoContest) return;
    const { start, end } = this.rangeInView();
    return this.$ngRedux.dispatch(
      PhotoAdvocacyApi.getCampaigns(this.property.UUID, start, end),
    ).then(() => {
      this.sectionDataRequestStatus.photoContest = true;
    });
  }

  fetchHomepageSection(): Promise<any> | undefined {
    if (this.sectionDataRequestStatus.superchargers2) return;
    return this.$ngRedux.dispatch(PropertyApi.getHomepageLayerData(this.property.UUID)).then(() => {
      this.sectionDataRequestStatus.superchargers2 = true;
    });
  }

  fetchQuoteListSection(): Promise<any> | undefined {
    if (this.sectionDataRequestStatus.superchargers3) return;
    return this.$ngRedux.dispatch(PropertyApi.getQuoteListLayerData(this.property.UUID)).then(() => {
      this.sectionDataRequestStatus.superchargers3 = true;
    });
  }

  fetchPrestaySection(): Promise<any> | undefined {
    if (this.sectionDataRequestStatus.prestay) return;
    this.$ngRedux.dispatch(JourneysActions.initialized(false));
    return Promise.all([
      this.$ngRedux.dispatch(PropertyApi.getAdvocacyGuestJourney(this.property.UUID)),
      this.$ngRedux.dispatch(PropertyApi.getPrestayLayerData(this.property.UUID)),
    ]).then(() => {
      this.sectionDataRequestStatus.prestay = true;
      this.$ngRedux.dispatch(JourneysActions.initialized(true));
      this.$ngRedux.dispatch(PropertyApi.getPhotoContestLayerData(this.property.UUID));
      const { start, end } = this.rangeInView();
      this.$ngRedux.dispatch(
        PrestayApi.getCampaigns(this.property.UUID, start, end),
      );
    });
  }

  fetchPoststaySection(): Promise<any> | undefined {
    if (this.sectionDataRequestStatus.poststay) return;
    return Promise.all([
      this.$ngRedux.dispatch(PropertyApi.getPoststayLayerData(this.property.UUID)),
    ]).then(() => {
      this.sectionDataRequestStatus.poststay = true;
      const { start, end } = this.rangeInView();
      this.$ngRedux.dispatch(
        PoststayApi.getCampaigns(this.property.UUID, start, end),
      );
    });
  }

  showCampaignDetails(layerName: string, campaignUuid: string): void {
    this.campaignContainer?.showExisting(this.property.UUID, campaignUuid, layerName);
  }

  newCampaign(layerName: string): void {
    this.campaignContainer?.createNew(this.property.UUID, layerName);
  }

  toggleSortMode(layerName: string): void {
    if (layerName === 'pre-stay') this.prestayCampaignsSortMode = !this.prestayCampaignsSortMode;
    if (layerName === 'photo-advocacy') this.photoAdvocacyCampaignsSortMode = !this.photoAdvocacyCampaignsSortMode;
  }

  saveSortOrder(layerName: string): void {
    if (layerName === 'pre-stay') {
      this.prestayCampaignsSortMode = false;
      const params = { propertyUUID: this.property.UUID, layerName: 'prestay' };
      const campaignIds = this.prestayCampaigns?.campaigns?.item
        ?.filter((c: any) => this.parse.bool(c.isActive) && this.parse.intOrDefault(c.type) !== 255)
        .map((c: any) => c.id) ?? [];
      this.campaign?.reorder(params, campaignIds);
    }
    if (layerName === 'photo-advocacy') {
      this.photoAdvocacyCampaignsSortMode = false;
      const params = { propertyUUID: this.property.UUID, layerName: 'photo-advocacy' };
      const campaignIds = this.photoCampaigns?.campaigns?.item
        ?.filter((c: any) => this.parse.bool(c.isActive) && this.parse.intOrDefault(c.type) !== 255)
        .map((c: any) => c.id) ?? [];
      this.campaign?.reorder(params, campaignIds);
    }
  }

  toggleLayer(layerName: string): void {
    this.$ngRedux.dispatch(JourneysActions.addLayer(this.spacesService.getCompanyUuid(), this.property.UUID, layerName));
  }

  onCalendarDateRangeChange(dateRange: { fromDate: Date; toDate: Date }): void {
    this.campaignRange = { start: dateRange.fromDate, end: dateRange.toDate };
    this.$ngRedux.dispatch(
      JourneysActions.dateRangeChange(this.property.UUID, this.dateSerializer(dateRange.fromDate), this.dateSerializer(dateRange.toDate)),
    );
  }

  onSectionsInitialized(controller: any): void {
    this.sectionsController = controller;
  }

  onCarouselCardsInitialized(controller: any): void {
    this.carouselCardsController = controller;
  }
}
